import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Star,
  QrCode,
  TrendingUp,
  MessageSquare,
  BarChart3,
  Bell,
  Smartphone,
  Heart,
  Mail,
  Users
} from "lucide-react";

export function ReviewsPage() {
  const features = [
    {
      icon: QrCode,
      title: "QR Code Collection",
      description: "Generate custom QR codes for instant review requests. Place them anywhere in your business for easy customer access."
    },
    {
      icon: Smartphone,
      title: "Mobile-Optimized Forms",
      description: "Beautiful, responsive review forms that work perfectly on any device. Make it effortless for customers to share feedback."
    },
    {
      icon: TrendingUp,
      title: "Sentiment Analysis",
      description: "AI-powered sentiment tracking to understand customer emotions at scale. Track trends and identify areas for improvement."
    },
    {
      icon: MessageSquare,
      title: "Google Business Profile Integration",
      description: "Seamlessly sync reviews with your Google Business Profile. Manage all your reviews from one central dashboard."
    },
    {
      icon: Bell,
      title: "Real-Time Notifications",
      description: "Get instant alerts when new reviews come in. Never miss an opportunity to engage with customer feedback."
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Deep insights into review trends, customer satisfaction scores, and funnel performance. Make data-driven decisions."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Create Review Form",
      description: "Design a custom review form with your branding and questions",
      icon: Star
    },
    {
      number: 2,
      title: "Generate QR Code",
      description: "Create QR codes or shareable links for easy distribution",
      icon: QrCode
    },
    {
      number: 3,
      title: "Collect Feedback",
      description: "Customers scan and submit reviews instantly from their phones",
      icon: Smartphone
    },
    {
      number: 4,
      title: "Analyze & Respond",
      description: "View sentiment analysis and engage with customer feedback",
      icon: TrendingUp
    }
  ];

  const relatedFeatures = [
    {
      title: "Loyalty & Rewards",
      description: "Turn satisfied reviewers into loyal customers with reward programs",
      icon: Heart,
      link: "/features/loyalty"
    },
    {
      title: "Marketing & Communications",
      description: "Follow up with email campaigns to engaged reviewers",
      icon: Mail,
      link: "/features/marketing"
    },
    {
      title: "CRM & Customer Data",
      description: "Organize customer data and segment based on feedback",
      icon: Users,
      link: "/features/crm"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Master Your Reputation with Reviews & Feedback"
        subtitle="Collect authentic customer reviews effortlessly with QR codes, mobile forms, and Google Business Profile integration. Turn feedback into your competitive advantage."
        primaryCTA={{ text: "Get Started Free", link: "/sign-up" }}
        secondaryCTA={{ text: "See Demo", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Key Features & Benefits"
        subtitle="Everything you need to collect, manage, and leverage customer reviews"
      >
        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Start collecting reviews in 4 simple steps"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="See Your Reviews Come to Life"
          description="Our intuitive dashboard gives you a complete view of all customer feedback. Track sentiment trends, monitor Google reviews, and identify opportunities for improvement. Real-time analytics help you understand what customers love and where to focus your efforts."
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Explore more ways to grow your business"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Ready to Build a 5-Star Reputation?"
          description="Join thousands of businesses using Tag-Inn to collect authentic reviews and improve customer satisfaction."
          primaryCTA={{ text: "Start Collecting Reviews", link: "/sign-up" }}
          secondaryCTA={{ text: "Contact Sales", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
